import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { MacroDonut, MacroBar } from "@/components/Macros";
import { Check } from "lucide-react";
import { TrackingHistory } from "@/components/TrackingHistory";
import { WaterTracker } from "@/components/WaterTracker";
import { WeeklyRecapCard } from "@/components/WeeklyRecapCard";

const isoToday = () => new Date().toISOString().slice(0, 10);

export function DailyTracking({ plan, todayPlan }) {
  const { t, tv } = useLang();
  const qc = useQueryClient();
  const date = isoToday();
  const members = plan?.members || [];
  const [memberId, setMemberId] = useState("primary");
  const mid = members.some((m) => m.member_id === memberId) ? memberId : (members[0]?.member_id || "primary");
  const member = members.find((m) => m.member_id === mid);

  const { data } = useQuery({ queryKey: ["tracking", date], queryFn: async () => (await api.get(`/tracking/${date}`)).data });
  const mine = data?.by_member?.[mid] || { consumed: [], totals: { calories: 0, protein: 0, carbs: 0, fat: 0 } };
  const target = todayPlan?.targets_by_member?.[mid] || member?.targets;
  const meals = (todayPlan?.meals || []).filter((m) => m.servings?.[mid] != null);

  const toggle = async (slot) => {
    try {
      const { data: res } = await api.post("/tracking/toggle", { date, slot, member_id: mid });
      qc.setQueryData(["tracking", date], (old) => ({ ...(old || {}), ...res }));
      qc.invalidateQueries({ queryKey: ["tracking-history"] });
      qc.invalidateQueries({ queryKey: ["food-day"] }); qc.invalidateQueries({ queryKey: ["food-history"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!target) return null;
  const remaining = Math.max(0, target.calories - mine.totals.calories);

  return (
    <div className="chenia-card p-5 sm:p-6 min-w-0 overflow-hidden" data-testid="dashboard-tracking">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="text-xl font-semibold">{t("tracking_title")}</h2>
        {members.length > 1 && (
          <div className="flex flex-wrap gap-1.5" data-testid="tracking-member-tabs">
            {members.map((m) => (
              <button key={m.member_id} data-testid={`tracking-member-${m.member_id}`} onClick={() => setMemberId(m.member_id)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${mid === m.member_id ? "bg-foreground text-background border-foreground" : "bg-card border-border hover:bg-secondary"}`}>
                {m.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-center min-w-0">
        <div className="flex flex-col items-center" data-testid="tracking-ring">
          <MacroDonut value={mine.totals.calories} max={target.calories} label={`${t("remaining")}: ${remaining} ${t("kcal")}`} sublabel={`/ ${target.calories} ${t("kcal")}`} />
        </div>
        <div className="space-y-3.5 w-full min-w-0">
          <MacroBar label={t("protein")} value={mine.totals.protein} unit={`g / ${target.protein} g`} pct={Math.min(100, Math.round((mine.totals.protein / target.protein) * 100))} color="#C87D65" />
          <MacroBar label={t("carbs")} value={mine.totals.carbs} unit={`g / ${target.carbs} g`} pct={Math.min(100, Math.round((mine.totals.carbs / target.carbs) * 100))} color="#A8BBA2" />
          <MacroBar label={t("fat")} value={mine.totals.fat} unit={`g / ${target.fat} g`} pct={Math.min(100, Math.round((mine.totals.fat / target.fat) * 100))} color="#E8B4B8" />
        </div>
      </div>

      <WaterTracker date={date} memberId={mid} glasses={data?.water?.[mid] || 0} goal={member?.water_goal || 8} />

      <div className="mt-5 space-y-2" data-testid="tracking-meals">
        {meals.length === 0 && <p className="text-sm text-muted-foreground">{t("not_planned_for")}</p>}
        {meals.map((m) => {
          const done = mine.consumed.includes(m.slot);
          const nut = m.nutrition_by_member?.[mid] || m.nutrition;
          return (
            <div key={m.slot} className={`flex items-center gap-3 p-2.5 rounded-2xl transition-colors ${done ? "bg-chenia-sage/20" : "hover:bg-secondary"}`} data-testid={`tracking-meal-${m.slot}`}>
              <button onClick={() => toggle(m.slot)} aria-label={t("mark_eaten")} data-testid={`tracking-toggle-${m.slot}`}
                className={`grid place-items-center h-9 w-9 rounded-full border-2 transition-colors shrink-0 ${done ? "bg-primary border-primary text-primary-foreground" : "border-border hover:border-primary"}`}>
                {done && <Check className="h-4 w-4" />}
              </button>
              <Link to={`/recipe/${m.recipe_id}`} className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t(m.slot)}</p>
                <p className={`font-medium truncate ${done ? "line-through text-muted-foreground" : ""}`}>{tv(m.name)}</p>
              </Link>
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold">{nut ? `${nut.calories} ${t("kcal")}` : "—"}</p>
                <p className="text-xs text-muted-foreground">{m.servings[mid]} {m.servings[mid] > 1 ? t("portions") : t("portion")}</p>
              </div>
            </div>
          );
        })}
      </div>

      <TrackingHistory memberId={mid} />
      <div className="mt-5"><WeeklyRecapCard members={members} memberId={mid} /></div>
    </div>
  );
}
